type LogValue = string | number | boolean | null;
type LogFields = Record<string, LogValue>;

const PROCESSING_KEYS = ['noiseSuppression', 'echoCancellation', 'autoGainControl'] as const;
const CAPABILITY_KEYS = [...PROCESSING_KEYS, 'voiceIsolation'] as const;

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const DEVICE_CLASSES: readonly (readonly [RegExp, string])[] = [
    [/bluetooth|airpods|buds|hands-?free/i, 'bluetooth'],
    [/\busb\b/i, 'usb'],
    [/headset|headphone|earpods|słuchawk|jabra|plantronics|\bplt\b|\bpoly\b/i, 'headset'],
    [/built-?in|internal|wbudowan|macbook/i, 'builtin'],
];

export const audioDeviceClass = (label: string | undefined): string => {
    if (!label) return 'unknown';
    return DEVICE_CLASSES.find(([pattern]) => pattern.test(label))?.[1] ?? 'other';
};

const safely = <T>(read: () => T): T | undefined => {
    try {
        return read();
    } catch {
        return undefined;
    }
};

const toScalar = (value: unknown): LogValue => {
    if (value === undefined || value === null) return null;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
    return safely(() => JSON.stringify(value)) ?? null;
};

const normalizeConstraint = (value: unknown): LogValue => {
    if (value && typeof value === 'object') {
        const { exact, ideal } = value as { exact?: unknown; ideal?: unknown };
        if (exact !== undefined) return toScalar(exact);
        if (ideal !== undefined) return toScalar(ideal);
    }
    return toScalar(value);
};

const joinList = (value: unknown): string | null => {
    if (Array.isArray(value)) return value.map(String).join(',');
    return value === undefined || value === null ? null : String(value);
};

export const describeLocalAudioTrack = (track: MediaStreamTrack): LogFields => {
    const fields: LogFields = {};

    const settings = safely(() => track.getSettings()) as Record<string, unknown> | undefined;
    for (const key of [...CAPABILITY_KEYS, 'channelCount', 'sampleRate', 'sampleSize', 'latency', 'deviceId', 'groupId']) {
        fields[`settings${capitalize(key)}`] = toScalar(settings?.[key]);
    }

    const constraints = safely(() => track.getConstraints()) as Record<string, unknown> | undefined;
    for (const key of PROCESSING_KEYS) {
        fields[`requested${capitalize(key)}`] = normalizeConstraint(constraints?.[key]);
    }

    const capabilities = safely(() =>
        typeof track.getCapabilities === 'function' ? track.getCapabilities() : undefined,
    ) as Record<string, unknown> | undefined;
    for (const key of CAPABILITY_KEYS) {
        fields[`caps${capitalize(key)}`] = joinList(capabilities?.[key]);
    }

    fields.deviceClass = audioDeviceClass(safely(() => track.label));
    fields.contentHint = toScalar(safely(() => track.contentHint));
    fields.readyState = toScalar(safely(() => track.readyState));
    fields.muted = toScalar(safely(() => track.muted));

    return fields;
};
